<script setup>
import AncientStudentService from "@/services/AncientStudentService"
import Step from "@/views/survey/Step.vue"
import FooterBlank from "@/layouts/components/FooterBlank.vue"
import ContentError from "@/components/common/ContentError.vue"

import { langTypes } from "@/utils/constant"
import { useSurveyHandle } from "@/composables/useSurvey"
import { useAuthStore } from "@/stores/authStore"
import { useRoute } from "vue-router"
import { router } from "@/plugins/1.router"

// ==================== START VARIABLE ======================
const CURRENT_YEAR = import.meta.env.VITE_CURRENT_YEAR
const { params } = useRoute()
const {
  dataSurvey,
  computedSteps,
  errorData,
  loading,
  buttonLoading,

  showCustomSwal,
} = useSurveyHandle("AncientStudent", AncientStudentService, params.id)

definePage({
  meta: {
    layout: "blank",
    public: true,
  },
})

const dataInfoBlocksAncientStudent = [
  {
    id: 1,
    type: "alert",
    position: "top",
    content: `
    <p class="text-error">${$gettext("Warning: If you remain on this page for more than 30 minutes, your answers may not be saved properly."
    )}
    </p>
    <p class="text-error">${$gettext("If you do not receive a confirmation email after submitting your form, it means your responses have not been recorded."
    )}
    </p>
    <p class="text-error">${$gettext("Please do not hesitate to contact us if you have any questions or difficulties."
    )}
    </p>
    `,
  },
  {
    id: 2,
    type: "alert",
    position: "bottom",
    content: `
    <p class="text-error">${$gettext("If you have not attended this program: please do not complete the form and contact us as soon as possible by sending us an email at: "
    )}
      
    <a class="text-blue" href="mailto:mastersurvey@eduniversal-group.com"><u>mastersurvey@eduniversal-group.com</u></a>
    ${$gettext("or by text / WhatsApp at ")}
    <a class="text-blue" href="tel:0630231266"><u>06 30 23 12 66</u></a> <span class="text-sky">/</span> <a class="text-blue" href="tel:0644154471"><u>06 44 15 44 71</u></a>
    </p>
    `,
  },
]
// ==================== START METHODS ======================
const redirectLogin = () => {
  router.push({ name: "index", query: { openLogin: "1" } })
}
const onChangeLang = (lang) => {
  const locale = langTypes.find((x) => x.id === lang)
  if (locale) {
    const urlObj = new URL(window.location.href)
    urlObj.searchParams.set("lang", locale.name)
    const newUrl = urlObj.toString()
    window.location.href = newUrl
  }
}
async function onSubmit(obj) {
  try {
    buttonLoading.value = true
    obj.hash = params.id
    const response = await AncientStudentService.postSurveyByStep({
      ...obj,
    })

    if (response) {
      await showCustomSwal({
        html: `
        <h3 class="text-center text-primary mb-2">${$gettext("Form submitted successfully."
        )}</h3>
        <p class="text-center mb-0">${$gettext("Thank you for your participation!"
        )}</p>
        `,
        showCancelButton: false,
        showConfirmButton: false,
        allowOutsideClick: false,
        showCloseButton: true,
        timer: 10000,
        width: 490,
        position: "center",
        timerProgressBar: true,
        willClose: () => {
          redirectLogin()
        },
      })
    }
  } catch (error) {
    errorData.value = error?.data
  } finally {
    buttonLoading.value = false
  }
}

const route = useRoute()
const CAMPAIGN = import.meta.env.VITE_CAMPAIGN
const isAdmin = computed(() => route.query.isAdmin)
const authStore = useAuthStore()

watch(() => +CAMPAIGN, async (newVal) => {
  if (newVal == 0) {
    if (isAdmin.value != 1) {
      await authStore.logout()
    }
  }
}, {immediate: true})
// ==================== END METHODS ========================
</script>

<template>
  <div class="register-student" style="min-height: 100vh">
    <div class="register-student__container">
      <header
        class="d-flex align-center justify-center px-6"
        style="position: relative"
      >
        <RouterLink to="/" class="app-logo app-title-wrapper">
          <img src="@/assets/logo.png" alt="Logo" />
        </RouterLink>
        <h1 class="title">
          {{ $gettext("Eduniversal Programs Surveys") }} {{ CURRENT_YEAR }}
        </h1>
        <BtnLang @onChange="onChangeLang" />
      </header>
      <main
        :class="[
          errorData?.code ? 'layout-error' : '',
          'register-student__main',
        ]"
      >
        <div style="max-width: 100%; margin: 0 auto; padding: 16px 0">
          <VCard class="form">
            <ContentError v-if="errorData?.code" :code="errorData?.code" />
            <template v-else>
              <div
                v-for="(item, index) in computedSteps"
                :key="item.id"
                :value="index"
              >
                <h1 style="font-weight: 800" class="text-center">
                  {{ dataSurvey.formType?.name }}
                </h1>
                <InfoBlock
                  v-for="item in dataInfoBlocksAncientStudent.filter(
                    (item) => item.position === 'top'
                  )"
                  :key="item.id"
                  :infoBlock="item"
                  class="my-3"
                />
                <h1 class="text-center">
                  {{ dataSurvey.program?.school?.name }}
                </h1>
                <h1 class="text-second-primary text-center">
                  {{ dataSurvey.program?.name }}
                </h1>

                <InfoBlock
                  v-for="item in dataInfoBlocksAncientStudent.filter(
                    (item) => item.position === 'bottom'
                  )"
                  :key="item.id"
                  :infoBlock="item"
                  class="my-3"
                />
                <div class="survey__main mt-3">
                  <div class="d-flex gap-6 flex-row-reverse survey__company">
                    <VCard class="flex-1 px-6 py-6">
                      <Waiting :show="loading" :isBlurFullScreen="false" />
                      <div
                        class="form-survey"
                        transition="fade-transition"
                        reverse-transition="fade-transition"
                      >
                        <h4 class="form-survey__title">
                          {{ item.title }}
                        </h4>
                        <!-- <pre>{{ item }}</pre> -->
                        <hr v-if="item.title" class="space" />

                        <!-- question -->
                        <Step
                          :step="item"
                          :loading="buttonLoading"
                          :survey="dataSurvey"
                          :isAncientStudent="true"
                          @update:modelValue="onSubmit($event)"
                        />
                      </div>
                    </VCard>
                  </div>
                </div>
              </div>
            </template>
          </VCard>
        </div>
      </main>
    </div>

    <VCard style="margin: 2rem 0" v-if="!errorData?.code">
      <ContactWithUs style="margin-bottom: 5rem" />
    </VCard>
    <FooterBlank :footerRh="true" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/pages/register-student.scss";
.register-recruiter__header {
  gap: 10px;
}
.survey {
  background-color: rgba(var(--v-theme-primary));
}

.print-pdf {
  padding: 12px 16px;
  max-width: 168px;
  height: fit-content;
}
.layout-error {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
// .title-survey {
//   font-size: 20px;
//   font-weight: 700;
// }
</style>
